import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { API } from '../config/api';
import {
  Radio, Play, Pause, Eye, TrendingUp, Newspaper, Star,
  ChevronRight, Sparkles, ArrowRight, Flame,
  Zap, Crown, BarChart3, Megaphone, Volume2, RefreshCw, AlertCircle
} from 'lucide-react';

// ─── Global audio state (one track at a time) ──────────
let _globalAudio = null;
let _globalSetPlaying = null;

function stopGlobalAudio() {
  if (_globalAudio) {
    _globalAudio.pause();
    _globalAudio.currentTime = 0;
    _globalAudio = null;
  }
  if (_globalSetPlaying) {
    _globalSetPlaying(null);
    _globalSetPlaying = null;
  }
}

function resolveMediaUrl(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  const base = (typeof API === 'string' ? API : '').replace('/api', '');
  if (url.startsWith('/')) return `${base}${url}`;
  return `${base}/${url}`;
}

// ═══════════════════════════════════════════════════════════
// HERO SECTION
// ═══════════════════════════════════════════════════════════
function HeroFeature({ hero, topArticle }) {
  const item = hero || topArticle;
  if (!item) return null;

  const title = item.title || 'DMV Soundstage';
  const desc = item.description || item.excerpt || '';
  const img = item.thumbnail_url || item.hero_url || item.cover_art_url || '';
  const badge = item.badge_text || (item.is_sponsored ? 'SPONSORED' : 'SPOTLIGHT');

  // Real destination — never /soundstage-spotlight (self-loop)
  let link = '/charts';
  if (item.external_url) link = item.external_url;
  else if (item.cta_url) link = item.cta_url;
  else if (item.article_id) link = `/news/${item.article_id}`;
  else if (item.track_id) link = `/track/${item.track_id}`;
  else if (item.video_id) link = `/video/${item.video_id}`;

  const ctaText = item.cta_text || (item.article_id ? 'Read More' : 'Explore');
  const hasMedia = !!(item.audio_url || item.file_url || item.video_url);
  const CtaIcon = hasMedia ? Play : ArrowRight;

  return (
    <Link to={link} className="block group" data-testid="hero-feature">
      <section className="relative w-full overflow-hidden" style={{ minHeight: '55vh' }}>
        {img && (
          <img
            src={img} alt=""
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            onError={e => { e.target.style.display = 'none'; }}
          />
        )}
        {!img && <div className="absolute inset-0 bg-gradient-to-br from-purple-950 via-black to-orange-950" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end h-full p-6 sm:p-10 lg:p-14" style={{ minHeight: '55vh' }}>
          <Badge className="bg-orange-500 text-white text-[11px] font-bold tracking-wider w-fit mb-3 uppercase" data-testid="hero-badge">
            <Sparkles className="w-3 h-3 mr-1" />{badge}
          </Badge>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-heading font-black text-white leading-[1.05] max-w-3xl mb-3 drop-shadow-2xl">
            {title}
          </h1>
          {desc && <p className="text-white/70 text-sm sm:text-base max-w-xl mb-5 line-clamp-2 drop-shadow-lg">{desc}</p>}
          <Button
            className="bg-white text-black hover:bg-orange-400 hover:text-black font-bold gap-2 w-fit text-sm px-6 py-2.5 rounded-full transition-all"
            data-testid="hero-cta"
          >
            <CtaIcon className="w-4 h-4 fill-current" /> {ctaText}
          </Button>
        </div>
      </section>
    </Link>
  );
}

// ═══════════════════════════════════════════════════════════
// LIVE NOW STRIP
// ═══════════════════════════════════════════════════════════
function LiveNowStrip({ streams }) {
  if (!streams?.length) return null;
  return (
    <section className="bg-red-950/30 border-y border-red-500/20 py-3 px-4 sm:px-8" data-testid="live-now-strip">
      <div className="max-w-7xl mx-auto flex items-center gap-4 overflow-x-auto scrollbar-hide">
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
          <span className="text-xs font-bold text-red-400 uppercase tracking-wider">Live Now</span>
        </div>
        {streams.map((s, i) => (
          <Link key={s.stream_id || i} to={`/watch/${s.stream_id}`}
            className="flex items-center gap-2.5 bg-black/40 hover:bg-red-500/20 border border-red-500/20 rounded-full pl-1 pr-3.5 py-1 flex-shrink-0 transition-colors"
            data-testid={`live-user-${i}`}>
            <div className="relative">
              {s.host_avatar ? (
                <img src={s.host_avatar} alt="" className="w-8 h-8 rounded-full object-cover ring-2 ring-red-500"
                  onError={e => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="w-8 h-8 rounded-full bg-red-500/30 flex items-center justify-center">
                  <Radio className="w-3.5 h-3.5 text-red-400" />
                </div>
              )}
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-red-500 rounded-full border-2 border-black animate-pulse" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-bold text-white truncate max-w-[100px]">{s.host_name || 'Live'}</p>
              <p className="text-[9px] text-red-300/70 flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" />{s.viewer_count || 0}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// FEED CARD — Real inline playback for tracks, navigation for video/replay
// ═══════════════════════════════════════════════════════════
function FeedCard({ item, type, rank, currentlyPlaying, setCurrentlyPlaying }) {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [playError, setPlayError] = useState(false);
  const title = item.title || '';
  const artist = item.artist_name || item.host_name || item.author_name || '';
  const img = item.cover_art_url || item.cover_url || item.thumbnail_url || '';
  const plays = item.plays || item.views || item.viewer_count || 0;
  const isTrending = rank && rank <= 3;
  const isSponsored = item.is_sponsored;

  // Determine media source
  const audioSrc = item.audio_url || item.file_url || item.preview_url || '';
  const videoSrc = item.video_url || item.file_url || '';
  const hasPlayableAudio = type === 'track' && !!audioSrc;
  const hasPlayableVideo = type === 'video' && !!videoSrc;
  const isReplay = type === 'replay';
  const isPlayable = hasPlayableAudio || hasPlayableVideo || isReplay;

  const itemId = item.track_id || item.video_id || item.stream_id || item.replay_id || '';
  const isPlaying = currentlyPlaying === itemId;

  // ── Play handler (tracks play inline, video/replay navigate) ──
  const handlePlay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPlayError(false);

    if (hasPlayableAudio) {
      if (isPlaying) {
        stopGlobalAudio();
        return;
      }
      stopGlobalAudio();
      const audio = new Audio(resolveMediaUrl(audioSrc));
      audio.crossOrigin = 'anonymous';
      audio.ontimeupdate = () => {
        if (audio.duration > 0) setProgress(audio.currentTime / audio.duration);
      };
      audio.onended = () => { setCurrentlyPlaying(null); setProgress(0); };
      audio.onerror = (err) => {
        console.error('[Spotlight] Audio error:', audioSrc, err);
        setPlayError(true);
        setCurrentlyPlaying(null);
      };
      audio.play()
        .then(() => {
          _globalAudio = audio;
          _globalSetPlaying = setCurrentlyPlaying;
          setCurrentlyPlaying(itemId);
        })
        .catch(err => {
          console.error('[Spotlight] Play rejected:', err.message);
          setPlayError(true);
        });
    } else if (hasPlayableVideo) {
      navigate(`/video/${item.video_id}`);
    } else if (isReplay) {
      navigate(`/replays/${item.replay_id || item.stream_id}`);
    }
  };

  // ── Card click = navigate to detail ──
  const handleCardClick = () => {
    if (type === 'track') navigate(`/track/${item.track_id}`);
    else if (type === 'video') navigate(`/video/${item.video_id}`);
    else if (type === 'article') navigate(`/news/${item.article_id}`);
    else if (type === 'replay') navigate(`/replays/${item.replay_id || item.stream_id}`);
  };

  // ── CTA label for non-playable ──
  const ctaLabel = type === 'article' ? 'Read' : type === 'video' ? 'Watch' : type === 'replay' ? 'Replay' : 'View';

  return (
    <div
      className="block group cursor-pointer"
      onClick={handleCardClick}
      data-testid={`feed-card-${type}`}
    >
      <div className="relative overflow-hidden rounded-xl bg-black/40 border border-white/5 hover:border-purple-500/30 transition-all">
        {/* Thumbnail area */}
        <div className="relative aspect-video overflow-hidden">
          {img ? (
            <img src={img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={e => { e.target.style.display = 'none'; }} />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-purple-900/40 to-black flex items-center justify-center">
              {isPlayable ? <Volume2 className="w-10 h-10 text-white/10" /> : <Newspaper className="w-10 h-10 text-white/10" />}
            </div>
          )}
          {/* Dark scrim */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />

          {/* ── PLAY BUTTON (always visible, centered, proper z-index) ── */}
          {isPlayable && (
            <button
              onClick={handlePlay}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              data-testid={`play-btn-${itemId}`}
            >
              <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all shadow-xl
                ${isPlaying
                  ? 'bg-orange-500 shadow-orange-500/40 scale-110'
                  : 'bg-black/50 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:scale-110 active:scale-95'
                }`}
              >
                {isPlaying
                  ? <Pause className="w-6 h-6 text-white" />
                  : <Play className="w-7 h-7 text-white fill-white ml-0.5" />
                }
              </div>
            </button>
          )}

          {/* Non-playable hover CTA (articles) */}
          {!isPlayable && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20
              opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <div className="bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2 border border-white/20">
                <Newspaper className="w-4 h-4 text-white" />
                <span className="text-sm text-white font-medium">{ctaLabel}</span>
              </div>
            </div>
          )}

          {/* Progress bar (when playing) */}
          {isPlaying && hasPlayableAudio && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10 z-30">
              <div className="h-full bg-orange-500 transition-[width] duration-200 ease-linear" style={{ width: `${progress * 100}%` }} />
            </div>
          )}

          {/* Play error indicator */}
          {playError && (
            <div className="absolute bottom-2 left-2 right-2 z-30 bg-red-900/80 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-300 flex-shrink-0" />
              <span className="text-[11px] text-red-200">Media unavailable</span>
            </div>
          )}

          {/* Top-left badges */}
          <div className="absolute top-2.5 left-2.5 flex gap-1.5 z-10 pointer-events-none">
            {isTrending && (
              <Badge className="bg-orange-500 text-white text-[10px] font-bold">
                <Flame className="w-3 h-3 mr-0.5" />#{rank}
              </Badge>
            )}
            {isSponsored && (
              <Badge className="bg-amber-500/90 text-black text-[10px] font-bold">Sponsored</Badge>
            )}
            {!isTrending && !isSponsored && type && (
              <Badge className="bg-white/10 backdrop-blur-sm text-white/90 text-[10px] font-medium capitalize">{type}</Badge>
            )}
            {isPlaying && (
              <Badge className="bg-orange-500 text-white text-[10px] font-bold animate-pulse">
                <Volume2 className="w-3 h-3 mr-0.5" /> Playing
              </Badge>
            )}
          </div>

          {/* Bottom-right plays count */}
          {plays > 0 && (
            <div className="absolute bottom-2.5 right-2.5 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1 z-10 pointer-events-none">
              <Eye className="w-3 h-3 text-white/70" />
              <span className="text-[10px] text-white/80 font-medium">{plays.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Title & artist */}
        <div className="p-3.5">
          <h3 className="text-sm sm:text-base font-bold text-white leading-tight line-clamp-2 group-hover:text-orange-300 transition-colors">
            {title}
          </h3>
          {artist && <p className="text-xs text-white/50 truncate mt-1">{artist}</p>}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SECTION HEADER
// ═══════════════════════════════════════════════════════════
function SectionHeader({ icon: Icon, title, iconColor, linkTo, linkText }) {
  return (
    <div className="flex items-center justify-between mb-4 px-1">
      <h2 className="text-base sm:text-lg font-heading font-black uppercase tracking-wide flex items-center gap-2">
        <Icon className={`w-5 h-5 ${iconColor}`} />
        {title}
      </h2>
      {linkTo && (
        <Link to={linkTo} className="text-xs text-white/40 hover:text-orange-400 font-semibold flex items-center gap-1 transition-colors">
          {linkText || 'See All'} <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// FEATURED ARTISTS ROW
// ═══════════════════════════════════════════════════════════
function FeaturedArtistsRow({ artists }) {
  if (!artists?.length) return null;
  return (
    <section className="mb-10 px-4 sm:px-8" data-testid="featured-artists-row">
      <SectionHeader icon={Crown} title="Featured Artists" iconColor="text-yellow-500" linkTo="/creatives" linkText="Discover" />
      <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2">
        {artists.map((a, i) => (
          <Link key={a.artist_id || i} to={`/u/${a.handle || a.user_id}`} className="flex-shrink-0 text-center group" data-testid={`artist-${i}`}>
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden ring-2 ring-yellow-500/20 group-hover:ring-yellow-500/60 transition-all mx-auto">
              {a.avatar_url ? (
                <img src={a.avatar_url} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-purple-600 to-orange-500" />
              )}
            </div>
            <p className="text-xs font-bold mt-2 truncate max-w-[80px] sm:max-w-[96px] group-hover:text-orange-300 transition-colors">{a.stage_name || a.name}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// NEWS CARD
// ═══════════════════════════════════════════════════════════
function NewsCard({ article, featured }) {
  if (!article) return null;
  return (
    <Link to={`/news/${article.article_id}`} className="block group" data-testid={featured ? 'news-featured' : 'news-card'}>
      <div className={`relative overflow-hidden rounded-xl ${featured ? 'aspect-[16/9]' : 'flex gap-3 items-start'}`}>
        {featured ? (
          <>
            {article.thumbnail_url ? (
              <img src={article.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                onError={e => { e.target.style.display = 'none'; }} />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-blue-900/50 to-purple-900/50" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute bottom-0 p-5 z-10">
              <Badge className="bg-purple-500/80 text-white text-[10px] uppercase mb-2">{article.category || 'News'}</Badge>
              <h3 className="text-lg sm:text-xl font-bold text-white leading-tight line-clamp-2">{article.title}</h3>
              <p className="text-xs text-white/50 mt-1.5">{article.author_name}</p>
            </div>
          </>
        ) : (
          <>
            <div className="w-28 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-black/40">
              {article.thumbnail_url ? (
                <img src={article.thumbnail_url} alt="" className="w-full h-full object-cover"
                  onError={e => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-purple-900/30 to-black flex items-center justify-center">
                  <Newspaper className="w-5 h-5 text-white/10" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 py-0.5">
              <h4 className="text-sm font-bold text-white line-clamp-2 leading-tight group-hover:text-orange-300 transition-colors">{article.title}</h4>
              <p className="text-[10px] text-white/30 mt-1">{article.author_name}</p>
            </div>
          </>
        )}
      </div>
    </Link>
  );
}

// ═══════════════════════════════════════════════════════════
// ANNOUNCEMENTS STRIP
// ═══════════════════════════════════════════════════════════
function AnnouncementStrip({ announcements }) {
  if (!announcements?.length) return null;
  return (
    <section className="mb-10 px-4 sm:px-8" data-testid="announcements-strip">
      <SectionHeader icon={Megaphone} title="Platform Updates" iconColor="text-green-400" />
      <div className="space-y-2">
        {announcements.map((a, i) => (
          <Link key={a.article_id || i} to={`/news/${a.article_id}`}
            className="flex items-center gap-3 p-3 rounded-lg bg-green-500/5 border border-green-500/10 hover:border-green-500/20 transition-colors"
            data-testid={`announcement-${i}`}>
            <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
              <Zap className="w-4 h-4 text-green-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate">{a.title}</p>
            </div>
            {a.pinned && <Badge className="bg-green-500/20 text-green-300 text-[9px] flex-shrink-0">Pinned</Badge>}
          </Link>
        ))}
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════
export default function SoundstageSpotlightPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      const res = await fetch(`${API}/promo/page-data`, { signal: controller.signal });
      clearTimeout(timer);
      if (res.ok) {
        setData(await res.json());
      } else {
        setFetchError(true);
      }
    } catch (err) {
      console.error('[Spotlight] Fetch failed:', err);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    return () => stopGlobalAudio();
  }, []);

  // ── Loading state ──
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-xs text-white/30 font-medium tracking-wider uppercase">Loading Spotlight</p>
        </div>
      </div>
    );
  }

  // ── Error state ──
  if (fetchError || !data) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-orange-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Couldn't load Spotlight</h2>
          <p className="text-sm text-white/40 mb-6 max-w-xs mx-auto">Something went wrong loading the page data. Tap below to try again.</p>
          <Button onClick={fetchData} className="bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="retry-btn">
            <RefreshCw className="w-4 h-4" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  const tracks = data.trending_tracks || [];
  const videos = data.trending_videos || [];
  const articles = data.articles || [];
  const announcements = data.announcements || [];
  const artists = data.featured_artists || [];
  const liveStreams = data.live_now || [];

  // Build mixed feed: videos first, then tracks
  const feedItems = [];
  videos.forEach((v, i) => feedItems.push({ ...v, _type: 'video', _rank: i + 1 }));
  tracks.forEach((t, i) => feedItems.push({ ...t, _type: 'track', _rank: i + 1 }));

  const featuredArticle = articles[0];
  const sideArticles = articles.slice(1, 5);

  // ── Empty state ──
  if (feedItems.length === 0 && articles.length === 0) {
    return (
      <div className="min-h-screen bg-black text-white" data-testid="soundstage-spotlight">
        <HeroFeature hero={data.hero} topArticle={null} />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <Sparkles className="w-12 h-12 text-orange-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">No content yet</h2>
          <p className="text-white/40 text-sm mb-6">Be the first to create something on DMV Soundstage.</p>
          <Link to="/feed">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white">Go to Feed</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="soundstage-spotlight">
      {/* Hero */}
      <HeroFeature hero={data.hero} topArticle={featuredArticle} />

      {/* Live Now */}
      <LiveNowStrip streams={liveStreams} />

      <div className="max-w-7xl mx-auto">
        {/* Featured Artists */}
        <div className="pt-8">
          <FeaturedArtistsRow artists={artists} />
        </div>

        {/* Trending Feed */}
        {feedItems.length > 0 && (
          <section className="mb-10 px-4 sm:px-8" data-testid="trending-feed">
            <SectionHeader icon={TrendingUp} title="Trending on Soundstage" iconColor="text-orange-500" linkTo="/charts" linkText="Charts" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {feedItems.slice(0, 9).map((item, i) => (
                <FeedCard
                  key={`${item._type}-${item.track_id || item.video_id || i}`}
                  item={item}
                  type={item._type}
                  rank={item._rank <= 3 ? item._rank : null}
                  currentlyPlaying={currentlyPlaying}
                  setCurrentlyPlaying={setCurrentlyPlaying}
                />
              ))}
            </div>
          </section>
        )}

        {/* News */}
        {articles.length > 0 && (
          <section className="mb-10 px-4 sm:px-8" data-testid="news-section">
            <SectionHeader icon={Newspaper} title="Soundstage News" iconColor="text-purple-400" />
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              <div className="lg:col-span-3">
                <NewsCard article={featuredArticle} featured />
              </div>
              <div className="lg:col-span-2 space-y-3">
                {sideArticles.map((a, i) => (
                  <NewsCard key={a.article_id || i} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Announcements */}
        <AnnouncementStrip announcements={announcements} />

        {/* More Content */}
        {feedItems.length > 9 && (
          <section className="mb-10 px-4 sm:px-8" data-testid="more-feed">
            <SectionHeader icon={Flame} title="More to Discover" iconColor="text-red-400" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {feedItems.slice(9).map((item, i) => (
                <FeedCard
                  key={`more-${item._type}-${item.track_id || item.video_id || i}`}
                  item={item}
                  type={item._type}
                  currentlyPlaying={currentlyPlaying}
                  setCurrentlyPlaying={setCurrentlyPlaying}
                />
              ))}
            </div>
          </section>
        )}

        {/* Bottom CTA */}
        <div className="text-center py-12 px-4">
          <p className="text-xs text-white/20 uppercase tracking-widest mb-3">DMV Soundstage</p>
          <p className="text-white/40 text-sm mb-4">The culture lives here.</p>
          <div className="flex justify-center gap-3 flex-wrap">
            <Link to="/live">
              <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-full gap-1.5">
                <Radio className="w-3.5 h-3.5" /> Watch Live
              </Button>
            </Link>
            <Link to="/charts">
              <Button variant="outline" size="sm" className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10 rounded-full gap-1.5">
                <BarChart3 className="w-3.5 h-3.5" /> Charts
              </Button>
            </Link>
            <Link to="/feed">
              <Button variant="outline" size="sm" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 rounded-full gap-1.5">
                <Star className="w-3.5 h-3.5" /> Feed
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
